import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Download } from "lucide-react";
import DieselDayDetail, { DieselStatsGrid } from "./DieselDayDetail";
import DieselItemForm from "./DieselItemForm";
import DieselByEquipment from "./DieselByEquipment";
import HiredEquipmentMaster from "./HiredEquipmentMaster";
import { SiteFilterSelect } from "./shared/SiteFilterSelect";
import { downloadExport } from "./shared/downloadExport";
import { NoAccess } from "../equipment/shared/NoAccess";
import { PaginationBar } from "@/components/shared/PaginationBar";

const HO_ROLES = ["Director", "Manager", "Back Office"];
const fmt = (n) => (n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });

function SeView() {
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [form, setForm] = useState(null);
  const [viewDay, setViewDay] = useState(null);

  const loadToday = () => {
    client.get("/diesel-register/today").then((res) => setToday(res.data)).catch((err) => toast.error(getErrorMessage(err)));
  };

  const loadHistory = (pageNum = 1) => {
    client
      .get("/diesel-register/history", { params: { page: pageNum, page_size: 50 } })
      .then((res) => {
        setHistory(res.data.items);
        setHistoryTotalPages(res.data.total_pages);
        setHistoryPage(res.data.page);
      })
      .catch(() => {});
  };

  useEffect(() => {
    loadToday();
    loadHistory(1);
  }, []);

  if (!today) return <p className="py-8 text-center text-slate-400">Loading...</p>;

  return (
    <div className="space-y-6" data-testid="diesel-se-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-slate-500">
          Site: <span className="font-medium text-slate-700">{today.display_label}</span> — Today,{" "}
          {new Date().toLocaleDateString("en-IN")}
        </p>
        <div className="flex gap-2">
          <Button data-testid="diesel-se-add-received" onClick={() => setForm({ kind: "received" })} className="bg-amber-600 hover:bg-amber-700">
            <Plus className="h-4 w-4 mr-1.5" /> Add Received
          </Button>
          <Button data-testid="diesel-se-add-issued" onClick={() => setForm({ kind: "issued" })} className="bg-amber-600 hover:bg-amber-700">
            <Plus className="h-4 w-4 mr-1.5" /> Add Issued
          </Button>
        </div>
      </div>

      <DieselStatsGrid day={today} />

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Today's Received ({today.received_items.length})</h3>
          {today.received_items.length === 0 ? (
            <p className="text-sm text-slate-400">No entries yet.</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {today.received_items.map((it) => (
                <li key={it.item_id} className="flex justify-between" data-testid={`diesel-se-received-${it.item_id}`}>
                  <span className="font-mono">{fmt(it.quantity)} L</span>
                  <span className="text-xs text-slate-500 truncate max-w-[200px]">{it.remarks || ""}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Today's Issued ({today.issued_items.length})</h3>
          {today.issued_items.length === 0 ? (
            <p className="text-sm text-slate-400">No entries yet.</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {today.issued_items.map((it) => (
                <li key={it.item_id} className="flex justify-between" data-testid={`diesel-se-issued-${it.item_id}`}>
                  <span className="text-xs">{it.equipment_label}</span>
                  <span className="font-mono">{fmt(it.quantity)} L</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-700">History (read-only)</h3>
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <Table data-testid="diesel-se-history-table">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Received</TableHead>
                <TableHead>Issued</TableHead>
                <TableHead>Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-6 text-slate-400">
                    No past entries.
                  </TableCell>
                </TableRow>
              ) : (
                history.map((d) => (
                  <TableRow key={d.id} data-testid={`diesel-se-history-row-${d.id}`} className="cursor-pointer" onClick={() => setViewDay(d.id)}>
                    <TableCell>{new Date(d.date).toLocaleDateString("en-IN")}</TableCell>
                    <TableCell className="font-mono">{fmt(d.received_today)} L</TableCell>
                    <TableCell className="font-mono">{fmt(d.issued_today)} L</TableCell>
                    <TableCell className={`font-mono ${d.balance < 0 ? "text-red-600" : ""}`}>{fmt(d.balance)} L</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        <PaginationBar page={historyPage} totalPages={historyTotalPages} onPageChange={loadHistory} testId="diesel-se-history-pagination" />
      </div>

      {form && (
        <DieselItemForm
          kind={form.kind}
          dayId={null}
          site={null}
          initial={null}
          onClose={() => setForm(null)}
          onSaved={() => {
            setForm(null);
            loadToday();
          }}
        />
      )}
      {viewDay && <DieselDayDetail dayId={viewDay} onClose={() => setViewDay(null)} />}
    </div>
  );
}

function HoView() {
  const [view, setView] = useState("byDate");
  const [sites, setSites] = useState([]);
  const [site, setSite] = useState("");
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewDay, setViewDay] = useState(null);
  const [manualForm, setManualForm] = useState(false);

  useEffect(() => {
    client.get("/diesel-register/sites").then((res) => setSites(res.data)).catch(() => {});
  }, []);

  const loadRegister = () => {
    if (!site) {
      setDays([]);
      return;
    }
    setLoading(true);
    client
      .get("/diesel-register/register", { params: { site } })
      .then((res) => setDays(res.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(loadRegister, [site]);

  const exportByDate = async () => {
    try {
      await downloadExport("/diesel-register/export/by-date", { site });
    } catch {
      toast.error("Export failed");
    }
  };

  const views = [
    { key: "byDate", label: "By Date", testId: "diesel-view-by-date" },
    { key: "byEquipment", label: "By Equipment", testId: "diesel-view-by-equipment" },
    { key: "master", label: "Hired Equipment Master", testId: "diesel-view-master" },
  ];

  return (
    <div className="space-y-4" data-testid="diesel-ho-view">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1.5">
          {views.map((v) => (
            <button
              key={v.key}
              data-testid={v.testId}
              onClick={() => setView(v.key)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                view === v.key ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
        {view !== "master" && <SiteFilterSelect sites={sites} value={site} onChange={setSite} testId="diesel-site-filter" placeholder="Select site" />}
        {view === "byDate" && site && (
          <Button variant="outline" onClick={exportByDate} data-testid="diesel-export-by-date">
            <Download className="h-4 w-4 mr-1.5" /> Export Excel
          </Button>
        )}
        {view !== "master" && (
          <Button data-testid="diesel-ho-add-entry" onClick={() => setManualForm(true)} className="bg-amber-600 hover:bg-amber-700 ml-auto">
            <Plus className="h-4 w-4 mr-1.5" /> Add entry (HO)
          </Button>
        )}
      </div>

      {view === "master" && <HiredEquipmentMaster />}
      {view === "byEquipment" && <DieselByEquipment site={site} />}
      {view === "byDate" && (
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <Table data-testid="diesel-register-table">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Received</TableHead>
                <TableHead>Issued</TableHead>
                <TableHead>Balance</TableHead>
                <TableHead>Total Received Till Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!site ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-400">
                    Select a site to view its Diesel Register.
                  </TableCell>
                </TableRow>
              ) : loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-400">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : days.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-400">
                    No entries found for this site.
                  </TableCell>
                </TableRow>
              ) : (
                days.map((d) => (
                  <TableRow key={d.id} data-testid={`diesel-register-row-${d.id}`} className="cursor-pointer" onClick={() => setViewDay(d.id)}>
                    <TableCell>{new Date(d.date).toLocaleDateString("en-IN")}</TableCell>
                    <TableCell className="font-mono">{fmt(d.received_today)} L</TableCell>
                    <TableCell className="font-mono">{fmt(d.issued_today)} L</TableCell>
                    <TableCell className={`font-mono ${d.balance < 0 ? "text-red-600" : ""}`}>{fmt(d.balance)} L</TableCell>
                    <TableCell className="font-mono">{fmt(d.total_received_till_date)} L</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {viewDay && <DieselDayDetail dayId={viewDay} onClose={() => setViewDay(null)} onChanged={loadRegister} />}
      {manualForm && (
        <DieselItemForm
          manual
          sites={sites}
          kind="received"
          dayId={null}
          initial={null}
          onClose={() => setManualForm(false)}
          onSaved={(data) => {
            setManualForm(false);
            if (data.site === site) loadRegister();
            else setSite(data.site);
          }}
        />
      )}
    </div>
  );
}

export default function DieselRegisterTab() {
  const { user } = useAuth();
  const isSE = user?.role === "Site Engineer";
  const isHO = HO_ROLES.includes(user?.role);
  if (!isSE && !isHO) return <NoAccess />;
  return <div data-testid="diesel-register-tab">{isSE ? <SeView /> : <HoView />}</div>;
}
